import Layout from "@/components/Layout";
import { formatTimeDisplayAmPm, TimePickerField } from "@/components/hr/TimePickerField";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  createWorkShift,
  deleteWorkShift,
  listWorkShifts,
  syncWorkShiftsFromApi,
  updateWorkShift,
  workShiftApiErrorMessage,
  type WorkShiftDraft,
  type WorkShiftRecord,
} from "@/lib/hr/workShiftsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Clock, Coffee, Edit, Filter, LayoutGrid, List, Loader2, Moon, Plus, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

const emptyDraft = (): WorkShiftDraft => ({
  name: "",
  startTime: "09:00",
  endTime: "17:00",
  breakMinutes: 60,
  crossesMidnight: false,
  active: true,
});

type ShiftSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "start_asc"
  | "start_desc"
  | "updated_latest"
  | "updated_oldest";

function shiftTimeLabel(s: WorkShiftRecord): string {
  const start = formatTimeDisplayAmPm(s.startTime);
  const end = formatTimeDisplayAmPm(s.endTime);
  const span = `${start} – ${end}${s.crossesMidnight ? " (+1)" : ""}`;
  return `${span} · ${s.breakMinutes}m break`;
}

const WorkShifts = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "work_shift_can_create");
  const canUpdate = can(user, "work_shift_can_update");
  const canDelete = can(user, "work_shift_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ShiftSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [filterNight, setFilterNight] = useState<"all" | "yes" | "no">("all");
  const [minBreak, setMinBreak] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WorkShiftRecord | null>(null);
  const [draft, setDraft] = useState<WorkShiftDraft>(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState<WorkShiftRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const shifts = useMemo(() => listWorkShifts(), [version]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncWorkShiftsFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        console.error("[WorkShifts] Load failed", error);
        if (mounted) toast.error("Could not fetch work shifts. Showing cached data if any.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const minBr = Number(minBreak);
    const hasMinBr = minBreak.trim() !== "" && Number.isFinite(minBr);

    return shifts.filter((s) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(s.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(s.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (nameNeedle && !s.name.toLowerCase().includes(nameNeedle)) return false;
      if (filterActive === "active" && !s.active) return false;
      if (filterActive === "inactive" && s.active) return false;
      if (filterNight === "yes" && !s.crossesMidnight) return false;
      if (filterNight === "no" && s.crossesMidnight) return false;
      if (hasMinBr && s.breakMinutes < minBr) return false;
      const matchesQuery =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.slug.toLowerCase().includes(q) ||
        shiftTimeLabel(s).toLowerCase().includes(q);
      return matchesQuery;
    });
  }, [
    shifts,
    query,
    filterCreatedDate,
    quickDate,
    filterName,
    filterActive,
    filterNight,
    minBreak,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "start_asc":
          return a.startTime.localeCompare(b.startTime);
        case "start_desc":
          return b.startTime.localeCompare(a.startTime);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterActive !== "all" ? 1 : 0) +
    (filterNight !== "all" ? 1 : 0) +
    (minBreak.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterActive("all");
    setFilterNight("all");
    setMinBreak("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterActive}|${filterNight}|${minBreak}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (s: WorkShiftRecord) => {
    setEditing(s);
    setDraft({
      name: s.name,
      startTime: s.startTime,
      endTime: s.endTime,
      breakMinutes: s.breakMinutes,
      crossesMidnight: s.crossesMidnight,
      active: s.active,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const saveShift = async () => {
    if (isSaving) return;
    if (!draft.name.trim()) {
      toast.error("Shift name is required.");
      return;
    }
    if (!draft.startTime.trim() || !draft.endTime.trim()) {
      toast.error("Start and end time are required.");
      return;
    }
    setIsSaving(true);
    try {
      const payload: WorkShiftDraft = {
        ...draft,
        name: draft.name.trim(),
        startTime: draft.startTime.trim(),
        endTime: draft.endTime.trim(),
        breakMinutes: Math.max(0, Math.min(24 * 60, Number(draft.breakMinutes) || 0)),
      };
      if (editing) {
        const result = await updateWorkShift(editing.id, payload);
        if (!result) {
          toast.error("Shift not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createWorkShift(payload);
        toast.success(result.message);
      }
      closeModal();
      refresh();
    } catch (error) {
      console.error("[WorkShifts] Save failed", error);
      toast.error(workShiftApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteWorkShift(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[WorkShifts] Delete failed", error);
      toast.error(workShiftApiErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Work shifts access required"
      message="You don’t have permission to view work shifts. Ask an administrator to grant work_shift_can_view_any."
    />
  ) : (
    <Layout title="Work shifts">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Work shifts</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Define attendance templates (hours, breaks) for employees.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add work shift
            </Button>
          ) : null}
        </div>

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit work shift" : "New work shift"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="glass-input h-10"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Morning shift"
                />
                <p className="text-[11px] text-muted-foreground">URL slug is generated automatically from this name when you save.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TimePickerField
                  label="Start time"
                  required
                  id="work-shift-start"
                  value={draft.startTime}
                  onChange={(next) => setDraft((d) => ({ ...d, startTime: next }))}
                />
                <TimePickerField
                  label="End time"
                  required
                  id="work-shift-end"
                  value={draft.endTime}
                  onChange={(next) => setDraft((d) => ({ ...d, endTime: next }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Break (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  max={1440}
                  className="glass-input h-10"
                  value={draft.breakMinutes}
                  onChange={(e) => setDraft((d) => ({ ...d, breakMinutes: Number(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex flex-col gap-3 rounded-lg border border-border/50 p-3 bg-muted/10">
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={draft.crossesMidnight} onCheckedChange={(c) => setDraft((d) => ({ ...d, crossesMidnight: c === true }))} id="crosses-midnight" />
                  <Label htmlFor="crosses-midnight" className="text-sm font-normal cursor-pointer">
                    End time is next calendar day (night shift)
                  </Label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={draft.active} onCheckedChange={(c) => setDraft((d) => ({ ...d, active: c !== false }))} id="shift-active" />
                  <Label htmlFor="shift-active" className="text-sm font-normal cursor-pointer">
                    Active (available for assignment)
                  </Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void saveShift()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, or hours…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter work shifts</SheetTitle>
                  <SheetDescription>Name, status, night shift, break length, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Shift</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input className="glass-input h-10" value={filterName} onChange={(e) => setFilterName(e.target.value)} autoComplete="off" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select value={filterActive} onValueChange={(v) => setFilterActive(v as typeof filterActive)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active only</SelectItem>
                          <SelectItem value="inactive">Inactive only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Crosses midnight</Label>
                      <Select value={filterNight} onValueChange={(v) => setFilterNight(v as typeof filterNight)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="yes">Yes (night)</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Min. break (minutes)</Label>
                      <Input className="glass-input h-10" value={minBreak} onChange={(e) => setMinBreak(e.target.value)} inputMode="numeric" placeholder="e.g. 30" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="shift-page-size-mobile" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as ShiftSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="start_asc">Start time: early</SelectItem>
                  <SelectItem value="start_desc">Start time: late</SelectItem>
                  <SelectItem value="updated_latest">Recently updated</SelectItem>
                  <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="shift-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as ShiftSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="start_asc">Start time: early</SelectItem>
                    <SelectItem value="start_desc">Start time: late</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading work shifts…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {shifts.length === 0 ? "No work shifts yet. Click “Add work shift” to create one." : "No shifts match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  title={s.name}
                  primaryMeta={{
                    label: "Hours",
                    value: shiftTimeLabel(s),
                    icon: s.crossesMidnight ? Moon : Clock,
                    iconClassName: s.crossesMidnight ? "text-violet-600" : "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: "Updated",
                    value: formatCatalogAuditDate(s.updatedAt),
                    icon: Coffee,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`${s.active ? "Active" : "Inactive"} · ${s.slug} · Created ${formatCatalogAuditDate(s.createdAt)}`}
                  onEdit={canUpdate ? () => openEdit(s) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(s) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    {s.crossesMidnight ? <Moon className="w-8 h-8 text-violet-500/70" /> : <Clock className="w-8 h-8 text-sky-600/50" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{s.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">{shiftTimeLabel(s)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {s.active ? "Active" : "Inactive"} · Updated {formatCatalogAuditDate(s.updatedAt)}
                    </p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-[11px] text-muted-foreground font-mono">{s.slug}</p>
                    <p className="text-[11px] text-muted-foreground">Created {formatCatalogAuditDate(s.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(s)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(s)}
                        aria-label="Delete shift"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this work shift?"
          description={
            deleteTarget ? (
              <>
                Remove <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? Assigned employees may lose this template reference later.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default WorkShifts;
